import { useEffect, useState } from 'react'

import cn from 'classnames'
import type { Hit } from 'instantsearch.js'

import ImageComponent from 'components/image'
import Link from 'components/link'
import BlurImage from 'components/product-card/blur-image'
import ProductTag from 'components/product-tag'
import SkeletonLoading from 'components/skeleton-loading'
import { usePutFavouriteBrand } from 'features/retailer/favourites/hooks/use-put-favourite-brand'

import { useTranslate } from 'hooks'
import { Brand } from 'hooks/common/use-brands'
import { useNavPromo } from 'hooks/use-nav-promo'

import { ROUTES } from 'constants/routes'

import FavouriteButton from '../favourite-button'
import { ProductItem } from '../hit-product-card/type'
import css from './hit-brand-card.module.scss'

type HitComponent = { hit: Hit<ProductItem> }

type HitBrandCardProps = {
  isBrandPreview?: boolean
  size?: 'md' | 'auto'
  className?: string
  brand?: Brand | undefined
  isLoading: boolean
  brandMovDisplay: string
  isLoggedIn: boolean
  isFavourite?: boolean
  openSignUp?: () => void
}

const HitBrandCard = ({
  isBrandPreview = false,
  hit,
  size = 'md',
  className,
  brand,
  brandMovDisplay,
  isLoading,
  isLoggedIn,
  isFavourite: _isFavourite = false,
  openSignUp,
}: HitBrandCardProps & HitComponent) => {
  const { t: tran } = useTranslate('common')
  const { t } = useTranslate('strapi-search-[...term]')
  const brandPagePath = ROUTES.BRAND.replace('[brandKey]', hit.attributes['brand-key'] as string)

  const brandInfo = brand?.brandPage

  const mainImage = brandInfo?.imgRef
    ? brandInfo.imgRef[0]
    : brandInfo?.logoRef
    ? brandInfo.logoRef
    : hit.images
    ? hit.images[0]
    : '/images/search/brand-card-placeholder.png'

  const brandLogoImage = brandInfo?.logoRef
    ? brandInfo.logoRef
    : brandInfo?.imgRef
    ? brandInfo.imgRef[0]
    : hit.images
    ? hit.images[0]
    : '/images/search/brand-card-placeholder.png'

  const [isFavourite, setIsFavourite] = useState<boolean>(_isFavourite)
  const { mutateAsync: setFavouriteBrand } = usePutFavouriteBrand(
    () => setIsFavourite(!isFavourite),
    () => null
  )

  useEffect(() => {
    setIsFavourite(_isFavourite)
  }, [_isFavourite])

  const promotion = useNavPromo(hit.attributes['brand-key'] as string)

  return (
    <Link
      className={cn(css.container, css[`container--${size}`], className)}
      href={isBrandPreview ? '#/' : brandPagePath}
      target="_blank"
    >
      <div className={css.cardTopWrapper}>
        <div className={css.imageContainer}>
          <div className={css.mainImageOverlay}>
            <FavouriteButton
              className={css.favouriteButton}
              isSolid={isFavourite}
              onToggle={isLoggedIn ? () => void setFavouriteBrand(hit.attributes['brand-key'] as string) : openSignUp}
            />
            <ProductTag
              promotion={promotion}
              onSalePercentage={brand?.isOnSale ? 1 : null}
              showPercentage={false}
              isNew={brand?.isNew}
              className={css.tag}
            />
          </div>
          <ImageComponent
            src={mainImage}
            className={cn([css.image, css.mainImage])}
            blurDataURL={BlurImage}
            placeholder="blur"
            alt={hit.name['en-US'] + tran('a11y.image')}
            objectFit="cover"
            width={318}
            height={318}
            priority={true}
          />
          <ImageComponent
            src={brandLogoImage}
            className={cn([css.image, css.brandImage])}
            blurDataURL={BlurImage}
            placeholder="blur"
            alt={hit.name['en-US'] + tran('a11y.image')}
            objectFit="cover"
            width={110}
            height={110}
            priority={true}
          />
        </div>
        <div className={css.titleWrapper}>
          <p className={css.brandName}>{hit.attributes['brand-name']}</p>
        </div>
      </div>

      <div className={css.cardBottomWrapper}>
        <div className={css.contentWrapper}>
          {isLoading ? (
            <div className={css.productPricesSkeletons}>
              <SkeletonLoading width="60%" />
              <SkeletonLoading width="40%" />
            </div>
          ) : (
            <div>
              <p className={css.description}>{brandInfo?.description}</p>
              {!!brandMovDisplay && (
                <p className={css.brandMov}>{t('BrandCard.BrandCard_min_order', { movDisplay: brandMovDisplay })}</p>
              )}
            </div>
          )}
        </div>

        {/* <div className={css.actionWrapper}>
          <div className={cn([css.btnGoToShop, !isHoverCard && css.hidden])}>
            <span>Shop</span>
            <IconArrowRightLong />
          </div>
        </div> */}
      </div>
      {/* <div onMouseEnter={handleHover} onMouseOut={handleUnHover} className={css.bkHover} /> */}
    </Link>
  )
}

export default HitBrandCard
